import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import apiDoc from "./apidocs.json";
import './swagger_style.css';
import Navigation from "../../components/Navigation";
import { FiSearch } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { Flowbite } from "flowbite-react";

// Add custom CSS to force green styling for the authorize button
const customStyles = `
  .swagger-ui .btn.authorize {
    background-color: rgba(5, 150, 105, 0.15) !important;
    border-color: #059669 !important;
    color: #059669 !important;
  }
  .swagger-ui .btn.authorize svg {
    fill: #f59e0b !important; /* Bright golden for locked state */
  }
  .swagger-ui .btn.authorize:hover {
    background-color: rgba(5, 150, 105, 0.25) !important;
  }
  .dark-mode .swagger-ui .btn.authorize {
    background-color: rgba(16, 185, 129, 0.15) !important;
    border-color: #10b981 !important;
    color: #10b981 !important;
  }
  .dark-mode .swagger-ui .btn.authorize svg {
    fill: #ffc107 !important; /* Bright golden for dark mode locked state */
  }
  
  /* Additional styles for the API lock icons */
  .swagger-ui .authorization__btn svg {
    fill: #f59e0b !important; /* Bright golden for locked */
  }
  .swagger-ui .authorization__btn.locked svg {
    fill: #f59e0b !important; /* Bright golden for locked */
  }
  .swagger-ui .authorization__btn.unlocked svg {
    fill: #d4af37 !important; /* Duller metallic gold for unlocked */
    transform: rotate(-10deg);
  }
  .dark-mode .swagger-ui .authorization__btn svg {
    fill: #ffc107 !important; /* Bright golden for dark mode locked */
  }
  .dark-mode .swagger-ui .authorization__btn.unlocked svg {
    fill: #d4af37 !important; /* Duller metallic gold for dark mode unlocked */
  }
  
  /* Make down arrow icons light grey */
  .swagger-ui .arrow,
  .swagger-ui button.arrow svg,
  .swagger-ui .arrow svg, 
  .swagger-ui button.expand-operation svg {
    fill: #94a3b8 !important;
    color: #94a3b8 !important;
  }
  .dark-mode .swagger-ui .arrow,
  .dark-mode .swagger-ui button.arrow svg,
  .dark-mode .swagger-ui .arrow svg,
  .dark-mode .swagger-ui button.expand-operation svg {
    fill: #64748b !important;
    color: #64748b !important;
  }
  
  /* Eliminate any white selection boxes */
  .swagger-ui .opblock.is-open .opblock-summary {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }
  .swagger-ui .opblock.is-open *:focus,
  .swagger-ui .opblock.is-open *:active {
    outline: none !important;
    box-shadow: none !important;
    border-color: var(--border-color) !important;
  }
`;

export default function FlavorDb2Playground() {
  const [searchTerm, setSearchTerm] = useState("");
  const { fetchProfile } = useAuth();
  const apiCallTimeoutRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.classList.contains('dark') || localStorage.getItem('theme') === 'dark';
  });
  
  // Ref to track consecutive toggle clicks
  const toggleClickCountRef = useRef(0);
  const themeToggleTimeoutRef = useRef(null);
  
  // State to track if theme toggle was just clicked to prevent the useEffect from overriding it
  const themeToggleClickedRef = useRef(false);

  // Handle theme changes from both the toggle and system preferences
  const handleThemeChange = useCallback(() => {
    if (!themeToggleClickedRef.current) {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    }
    themeToggleClickedRef.current = false;
  }, []);

  useEffect(() => {
    // Listen for theme changes on the document element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    handleThemeChange();
    
    return () => observer.disconnect();
  }, [handleThemeChange]);

  // Apply theme class to Swagger UI
  useEffect(() => {
    document.documentElement.dataset.swaggerTheme = isDarkMode ? 'dark' : 'light';
    
    const swaggerContainer = document.querySelector('.swagger-container');
    if (swaggerContainer) {
      if (isDarkMode) {
        swaggerContainer.classList.add('dark-mode');
      } else {
        swaggerContainer.classList.remove('dark-mode');
      }
    }
  }, [isDarkMode]);

  // Force application of custom styles to the authorize button
  useEffect(() => {
    // Check if style element already exists
    const existingStyle = document.getElementById('swagger-custom-styles');
    if (!existingStyle) {
      const styleElement = document.createElement('style');
      styleElement.id = 'swagger-custom-styles';
      styleElement.innerHTML = customStyles;
      document.head.appendChild(styleElement);
    }

    // Helper function to replace lock icon with unlock icon
    const replaceWithUnlockIcon = (svg) => {
      const pathElement = svg.querySelector('path');
      if (pathElement) {
        // Use a path that represents an open lock
        pathElement.setAttribute('d', 'M7 11V7C7 4.24 9.24 2 12 2S17 4.24 17 7v2h-2V7c0-1.66-1.34-3-3-3S9 5.34 9 7v4h8c1.1 0 2 .9 2 2v7c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2v-7c0-1.1.9-2 2-2z');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.style.transform = 'rotate(-20deg)';
        svg.style.transformOrigin = 'center';
      }
    };

    // Helper function to ensure closed lock icon
    const replaceWithLockIcon = (svg) => {
      const pathElement = svg.querySelector('path');
      if (pathElement) {
        // Standard lock icon path
        pathElement.setAttribute('d', 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.style.transform = 'none';
      }
    };

    // Apply styles to any authorize buttons after component loads
    const applyButtonStyles = () => {
      // Style the main authorize button
      const authButtons = document.querySelectorAll('.swagger-ui .btn.authorize');
      authButtons.forEach(button => {
        button.style.backgroundColor = isDarkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(5, 150, 105, 0.15)';
        button.style.borderColor = isDarkMode ? '#10b981' : '#059669';
        button.style.color = isDarkMode ? '#10b981' : '#059669';
        
        // Find and style the SVG if it exists
        const svg = button.querySelector('svg');
        if (svg) {
          svg.style.fill = isDarkMode ? '#ffc107' : '#f59e0b';
          
          // Check if this button is in an authorized state
          const isAuthorized = button.closest('.authorized') || button.classList.contains('authorized');
          if (isAuthorized) {
            replaceWithUnlockIcon(svg);
          } else {
            replaceWithLockIcon(svg);
          }
        }
      });

      // Apply colors to all lock icons in API operations and replace with unlock icon when authorized
      const lockIcons = document.querySelectorAll('.swagger-ui .authorization__btn svg, .swagger-ui .opblock-summary-control .authorization__btn svg');
      lockIcons.forEach(icon => {
        // Check if the parent has an "unlocked" class to determine icon state
        const isUnlocked = icon.closest('.authorization__btn')?.classList.contains('unlocked');
        
        if (isUnlocked) {
          // Replace with unlock icon
          replaceWithUnlockIcon(icon);
          icon.style.fill = isDarkMode ? '#d4af37' : '#d4af37'; // Duller gold for unlocked
        } else {
          // Replace with lock icon
          replaceWithLockIcon(icon);
          icon.style.fill = isDarkMode ? '#ffc107' : '#f59e0b'; // Bright golden for locked
        }
      });
      
      // Make arrow icons grey
      const arrowIcons = document.querySelectorAll('.swagger-ui .arrow, .swagger-ui button.arrow svg, .swagger-ui button.expand-operation svg');
      arrowIcons.forEach(arrow => {
        arrow.style.fill = isDarkMode ? '#64748b' : '#94a3b8';
        arrow.style.color = isDarkMode ? '#64748b' : '#94a3b8';
      });
      
      // Remove any selection outlines after interaction
      document.querySelectorAll('.swagger-ui .opblock.is-open .opblock-summary').forEach(element => {
        element.style.outline = 'none';
        element.style.boxShadow = 'none';
        element.style.border = 'none';
      });
    };

    // Initial application and setup mutation observer to catch dynamically added buttons and auth state changes
    setTimeout(applyButtonStyles, 500);
    
    // Run again after 1 second to catch components that might load later
    setTimeout(applyButtonStyles, 1000);
    setTimeout(applyButtonStyles, 2000);

    const observer = new MutationObserver((mutations) => {
      // Additional check to see if authority status has changed
      let hasAuthStateChanged = false;
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class' && 
            (mutation.target.classList.contains('unlocked') || 
             mutation.target.classList.contains('authorized'))) {
          hasAuthStateChanged = true;
        }
      });
      
      // Apply button styles if auth state has changed
      if (hasAuthStateChanged) {
        setTimeout(applyButtonStyles, 10);
      }
      
      // Regular application for other DOM changes
      applyButtonStyles();
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['class'] // Watch for class changes that might indicate auth state change
    });

    return () => observer.disconnect();
  }, [isDarkMode]);

  // Create filtered API docs based on search term
  const filteredApiDoc = useMemo(() => {
    if (!searchTerm) return apiDoc;

    const filteredPaths = Object.keys(apiDoc.paths).reduce((acc, path) => {
      if (path.toLowerCase().includes(searchTerm.toLowerCase())) {
        acc[path] = apiDoc.paths[path];
      }
      return acc;
    }, {});
  
    return { ...apiDoc, paths: filteredPaths };
  }, [searchTerm]);

  // Handle successful API responses by updating user profile
  const handleApiResponse = (res) => {
    if (apiCallTimeoutRef.current) {
      clearTimeout(apiCallTimeoutRef.current);
    }

    if (res.status >= 200 && res.status < 300) {
      // Use a short delay to ensure the backend has processed the request
      apiCallTimeoutRef.current = setTimeout(() => {
        fetchProfile();
      }, 500);
    }
    return res;
  };

  return (
    <Flowbite theme={{ dark: isDarkMode }}>
      <div className="relative">
        <Navigation stay={true} />
        
        <div className={`swagger-container ${isDarkMode ? 'dark-mode' : ''}`}>
          {/* Inline style tag as additional safeguard */}
          <style dangerouslySetInnerHTML={{ __html: customStyles }} />
          
          <div className={`swagger-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className="search-container">
              <div className="search-wrapper">
                <FiSearch className="search-icon" size={20} />
                <input
                  type="text"
                  placeholder="Search endpoints"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="api-search-input"
                />
              </div>
            </div>
            
            <SwaggerUI
              spec={filteredApiDoc}
              requestInterceptor={(req) => {
                if (req.headers.Authorization && !req.headers.Authorization.startsWith("Bearer ")) {
                  req.headers.Authorization = `Bearer ${req.headers.Authorization}`;
                }
                return req;
              }}
              responseInterceptor={handleApiResponse}
            />
          </div>
        </div>
      </div>
    </Flowbite>
  );
}